import React from 'react'
import { Navigate, Outlet, RouteObject, createBrowserRouter } from 'react-router-dom'

import { initializeCache } from '../../models/RecordFactory'
import { RecordFactory } from '../../models/RecordFactory'
import { SQLiteFactory } from '../../models/SQLiteFactory'
import { OnboardingProvider } from '../../providers/OnboardingProvider'
import { IS_DEVELOPMENT, IS_STAGING } from '../../utils/constants'
import { BlockSchedule } from '../bookings/BlockSchedule'
import { BookingPage } from '../bookings/BookingPage'
import { BookingWorkflow } from '../bookings/BookingWorkflow'
import { EventPage } from '../bookings/EventPage'
import { SchedulePage } from '../calendar/SchedulePage'
import { ClientPage } from '../contacts/ClientPage'
import { ClientsPage } from '../contacts/ClientsPage'
import { PayeePage } from '../contacts/PayeePage'
import { SubaccountPage } from '../contacts/SubaccountPage'
import { SubaccountWorkflow } from '../contacts/SubaccountWorkflow'
import { UserWorkflow } from '../contacts/UserWorkflow'
import {
    DashboardDetailDepositsPage,
    loader as dashboardDetailDepositsPageLoader,
} from '../dashboards/DashboardDetailDepositsPage'
import { DashboardDetailPage } from '../dashboards/DashboardDetailPage'
import { DashboardPage } from '../dashboards/DashboardPage'
import { RequestCallPage } from '../dashboards/RequestCallPage'
import { AlertASAPPage } from '../dashboards/alerts/AlertASAPPage'
import { AlertAffiliatePage } from '../dashboards/alerts/AlertAffiliatePage'
import { AlertReaderPage } from '../dashboards/alerts/AlertReaderPage'
import { AlertTeamPage } from '../dashboards/alerts/AlertTeamPage'
import { AlertsRoot } from '../dashboards/alerts/AlertsRoot'
import { NPSWorkflowCoordinator } from '../dashboards/nps/NPSWorkflowCoordinator'
import { DowngradeConfirmationPage } from '../downgrade/DowngradeConfirmationPage'
import { DowngradeDiscountPage } from '../downgrade/DowngradeDiscountPage'
import { DowngradeFeaturesPage } from '../downgrade/DowngradeFeaturesPage'
import { DowngradeLitePage } from '../downgrade/DowngradeLitePage'
import { DowngradePlanPage } from '../downgrade/DowngradePlanPage'
import { DowngradeReasonPage } from '../downgrade/DowngradeReasonPage'
import { AdjustmentHistoryPage } from '../items/AdjustmentHistoryPage'
import { AdjustmentPage } from '../items/AdjustmentPage'
import { BaseRecordPage } from '../items/BaseRecordPage'
import { CategoryWorkflow } from '../items/CategoryWorkflow'
import { ClientFieldPage } from '../items/ClientFieldPage'
import { ContractWorkflow } from '../items/ContractWorkflow'
import { FieldPage } from '../items/FieldPage'
import { FieldsPage } from '../items/FieldsPage'
import { FixedReservationPage } from '../items/FixedReservationPage'
import { GiftPage } from '../items/GiftPage'
import { GiftWorkflow } from '../items/GiftWorkflow'
import { ItemWorkflow } from '../items/ItemWorkflow'
import { LessonPage } from '../items/LessonPage'
import { NewSendRecordPage } from '../items/NewSendRecordPage'
import { NewSubscriptionTotalPage } from '../items/NewSubscriptionTotalPage'
import { PackagePage } from '../items/PackagePage'
import { PackageWorkflow } from '../items/PackageWorkflow'
import { PaymentsPage } from '../items/PaymentsPage'
import { RecordPage } from '../items/RecordPage'
import { SetupInventoryPage } from '../items/SetupInventoryPage'
import { SignaturePage } from '../items/SignaturePage'
import { SubscriptionPage } from '../items/SubscriptionPage'
import { SubscriptionWorkflow } from '../items/SubscriptionWorkflow'
import { TypeWorkflow } from '../items/TypeWorkflow'
import { CodeEntry } from '../login/CodeEntry'
import { ComebackAccountSwitcher } from '../login/ComebackAccountSwitcher'
import { Login } from '../login/Login'
import { LoginPage } from '../login/LoginPage'
import { Logout } from '../login/Logout'
import { SignupWorkflow } from '../login/SignupWorkflow'
import { MessagesPageWrapper, loader as messagesPageLoader } from '../messages/MessagesPage'
import { MessageThreadWrapper, loader as messageThreadLoader } from '../messages/ThreadView'
import { OnboardingRoot } from '../onboarding/OnboardingRoot'
import { SetupDashboardCompletePage } from '../onboarding/SetupDashboardCompletePage'
import { SetupDashboardPayoutPage } from '../onboarding/SetupDashboardPayoutPage'
import { SetupDashboardPremiumNumberPage } from '../onboarding/SetupDashboardPremiumNumberPage'
import { SetupDashboardVerifyBusinessPage } from '../onboarding/SetupDashboardVerifyBusinessPage'
import { SetupDashboardVerifyCompletePage } from '../onboarding/SetupDashboardVerifyCompletePage'
import { SetupDashboardVerifyPersonalPage } from '../onboarding/SetupDashboardVerifyPersonalPage'
import { SetupDashboardVerifyPhotoPage } from '../onboarding/SetupDashboardVerifyPhotoPage'
import { SetupDashboardVerifyWelcomePage } from '../onboarding/SetupDashboardVerifyWelcomePage'
import { SetupOnboardingBusinessHoursPage } from '../onboarding/SetupOnboardingBusinessHoursPage'
import { SetupOnboardingCategoriesPage } from '../onboarding/SetupOnboardingCategoriesPage'
import { SetupOnboardingClientIntakePage } from '../onboarding/SetupOnboardingClientIntakePage'
import { SetupOnboardingContractsPage } from '../onboarding/SetupOnboardingContractsPage'
import { SetupOnboardingCopyDataPage } from '../onboarding/SetupOnboardingCopyDataPage'
import { SetupOnboardingItemsPage } from '../onboarding/SetupOnboardingItemsPage'
import { SetupOnboardingLocationPage } from '../onboarding/SetupOnboardingLocationPage'
import { SetupOnboardingPaymentsContinuedPage } from '../onboarding/SetupOnboardingPaymentsContinuedPage'
import { SetupOnboardingPaymentsPage } from '../onboarding/SetupOnboardingPaymentsPage'
import { SetupOnboardingProfilePage } from '../onboarding/SetupOnboardingProfilePage'
import { SetupOnboardingSuccessPage } from '../onboarding/SetupOnboardingSuccessPage'
import { SetupOnboardingTypesPage } from '../onboarding/SetupOnboardingTypesPage'
import { ChargePage } from '../payments/ChargePage'
import { ChargeWorkflow } from '../payments/ChargeWorkflow'
import { ClientCardPage } from '../payments/ClientCardPage'
import { DepositPage } from '../payments/DepositPage'
import { EstimatePage } from '../payments/EstimatePage'
import { EstimateWorkflow } from '../payments/EstimateWorkflow'
import { InvoicePage } from '../payments/InvoicePage'
import { InvoiceWorkflow } from '../payments/InvoiceWorkflow'
import { OrderReaderPage } from '../payments/OrderReaderPage'
import { PaymentPage } from '../payments/PaymentPage'
import { ReaderPage } from '../payments/ReaderPage'
import { ReadersPage } from '../payments/ReadersPage'
import { SourcesPage } from '../payments/SourcesPage'
import { ReportDetailsPage } from '../reports/ReportDetailsPage'
import { ReportRunnerPage } from '../reports/ReportRunnerPage'
import { ExternalCalendarSelectPage } from '../settings/ExternalCalendarSelectPage'
import { LinkPage } from '../settings/LinkPage'
import { NumbersPage } from '../settings/NumbersPage'
import { PrivacyAndTermsPage } from '../settings/PrivacyAndTermsPage'
import { SelectPlanPage } from '../settings/SelectPlanPage'
import { SendContract } from '../settings/SendContract'
import { SendTaxFormPage } from '../settings/SendTaxFormPage'
import { SettingsPage } from '../settings/SettingsPage'
import { SetupCalendarSyncPage } from '../settings/SetupCalendarSyncPage'
import { SetupCancellationPage } from '../settings/SetupCancellationPage'
import { SetupCategoriesPage } from '../settings/SetupCategoriesPage'
import { SetupContractsPage } from '../settings/SetupContractsPage'
import { SetupDataExportPage } from '../settings/SetupDataExportPage'
import { SetupDefaultCategoriesPage } from '../settings/SetupDefaultCategoriesPage'
import { SetupDefaultContractsPage } from '../settings/SetupDefaultContractsPage'
import { SetupDefaultFormsPage } from '../settings/SetupDefaultFormsPage'
import { SetupDefaultItemsPage } from '../settings/SetupDefaultItemsPage'
import { SetupDescriptionsPage } from '../settings/SetupDescriptionsPage'
import { SetupFeaturesPage } from '../settings/SetupFeaturesPage'
import { SetupGratuityPage } from '../settings/SetupGratuityPage'
import { SetupItemsPage } from '../settings/SetupItemsPage'
import { SetupItemsSortPage } from '../settings/SetupItemsSortPage'
import { SetupLessonsPage } from '../settings/SetupLessonsPage'
import { SetupLocationPage } from '../settings/SetupLocationPage'
import { SetupNotificationsPage } from '../settings/SetupNotificationsPage'
import { SetupOnlineChatPage } from '../settings/SetupOnlineChatPage'
import { SetupOnlineLeadPage } from '../settings/SetupOnlineLeadPage'
import { SetupOnlinePaymentPage } from '../settings/SetupOnlinePaymentPage'
import { SetupPayoutsPage } from '../settings/SetupPayoutsPage'
import { SetupProfilePage } from '../settings/SetupProfilePage'
import { SetupSalesTaxPage } from '../settings/SetupSalesTaxPage'
import { SetupSchedulingPage } from '../settings/SetupSchedulingPage'
import { SetupSuiteLinkPage } from '../settings/SetupSuiteLinkPage'
import { SetupTypesPage } from '../settings/SetupTypesPage'
import { SetupVideoConferencePage } from '../settings/SetupVideoConferencePage'
import { TutorialsPage } from '../settings/TutorialsPage'
import App from './App'
import ErrorRouteNotFound from './ErrorRouteNotFound'
import NavigationRoot from './NavigationRoot'
import { NewUserSelectPlan } from './NewUserSelectPlan'
import { PSApplication } from './PSApplication'
import { designRoutes } from './design/Design'

function PlanRoute({ children }: React.PropsWithChildren<any>) {
    const user = PSApplication.me()

    if (!user.plan) return null

    return children
}

function ProtectedRoute({ children }: React.PropsWithChildren<any>) {
    const user = PSApplication.me()

    if (user?.role === 'owner' || user?.role === 'admin') {
        return children
    }
    else if (user?.plan === 'franchise') {
        return <ReportRunnerPage />
    }

    return <Navigate to="/clients" />
}

// Settings Routes

const accountRoutes: RouteObject[] = [
    { path: 'account/features', element: <SetupFeaturesPage /> },
    { path: 'account/profile', element: <SetupProfilePage /> },
    { path: 'account/location', element: <SetupLocationPage /> },
    { path: 'account/numbers', element: <NumbersPage /> },
    { path: 'account/number/edit', element: <SetupDashboardPremiumNumberPage /> },
    { path: 'account/number/:teamMemberID/new', element: <SetupDashboardPremiumNumberPage /> },
    {
        path: 'account/number/:phoneNumberID/edit',
        element: <SetupDashboardPremiumNumberPage />,
    },
    {
        path: 'account/memos',
        element: <SetupDescriptionsPage />,
    },
    {
        path: 'account/notifications',
        element: <SetupNotificationsPage />,
    },
]

const servicesRoutes: RouteObject[] = [
    { path: 'services', element: <SetupItemsPage type="service" /> },
    { path: 'services/new', element: <ItemWorkflow action="new" type="service" /> },
    { path: 'services/:id/edit', element: <ItemWorkflow action="edit" type="service" /> },
    { path: 'services/:id/copy', element: <ItemWorkflow action="copy" type="service" /> },
    { path: 'services/defaults', element: <SetupDefaultItemsPage type="service" /> },
]

const classesRoutes: RouteObject[] = [
    { path: 'classes', element: <SetupItemsPage type="class" /> },
    { path: 'classes/new', element: <ItemWorkflow action="new" type="class" /> },
    { path: 'classes/:id/edit', element: <ItemWorkflow action="edit" type="class" /> },
    { path: 'classes/:id/copy', element: <ItemWorkflow action="copy" type="class" /> },
    { path: 'classes/:id/lessons', element: <SetupLessonsPage /> },
    { path: 'classes/defaults', element: <SetupDefaultItemsPage type="class" /> },
]

const reservationsRoutes: RouteObject[] = [
    { path: 'reservations', element: <SetupItemsPage type="reservation" /> },
    { path: 'reservations/new', element: <ItemWorkflow action="new" type="reservation" /> },
    { path: 'reservations/:id/edit', element: <ItemWorkflow action="edit" type="reservation" /> },
    { path: 'reservations/:id/copy', element: <ItemWorkflow action="copy" type="reservation" /> },
    { path: 'reservations/defaults', element: <SetupDefaultItemsPage type="reservation" /> },
]

const productsRoutes: RouteObject[] = [
    { path: 'products', element: <SetupItemsPage type="product" /> },
    { path: 'products/new', element: <ItemWorkflow action="new" type="product" /> },
    { path: 'products/:id/edit', element: <ItemWorkflow action="edit" type="product" /> },
    { path: 'products/:id/copy', element: <ItemWorkflow action="copy" type="product" /> },
    { path: 'products/defaults', element: <SetupDefaultItemsPage type="product" /> },
]

const packagesRoutes: RouteObject[] = [
    { path: 'packages', element: <SetupItemsPage type="package" /> },
    { path: 'packages/new', element: <ItemWorkflow action="new" type="package" /> },
    { path: 'packages/:id/edit', element: <ItemWorkflow action="edit" type="package" /> },
    { path: 'packages/:id/copy', element: <ItemWorkflow action="copy" type="package" /> },
    { path: 'packages/defaults', element: <SetupDefaultItemsPage type="package" /> },
]

const subscriptionsRoutes: RouteObject[] = [
    { path: 'subscriptions', element: <SetupItemsPage type="subscription" /> },
    { path: 'subscriptions/new', element: <ItemWorkflow action="new" type="subscription" /> },
    {
        path: 'subscriptions/:id/edit',
        element: <ItemWorkflow action="edit" type="subscription" />,
    },
    {
        path: 'subscriptions/:id/copy',
        element: <ItemWorkflow action="copy" type="subscription" />,
    },
    { path: 'subscriptions/defaults', element: <SetupDefaultItemsPage type="subscription" /> },
]

const giftsRoutes: RouteObject[] = [
    { path: 'gifts', element: <SetupItemsPage type="gift" /> },
    { path: 'gifts/new', element: <ItemWorkflow action="new" type="gift" /> },
    { path: 'gifts/:id/edit', element: <ItemWorkflow action="edit" type="gift" /> },
    { path: 'gifts/:id/copy', element: <ItemWorkflow action="copy" type="gift" /> },
    { path: 'gifts/defaults', element: <SetupDefaultItemsPage type="gift" /> },
]

const categoriesRoutes: RouteObject[] = [
    { path: 'categories', element: <SetupCategoriesPage /> },
    { path: 'categories/new', element: <CategoryWorkflow action="new" /> },
    { path: 'categories/:id/edit', element: <CategoryWorkflow action="edit" /> },
    { path: 'categories/:id/copy', element: <CategoryWorkflow action="copy" /> },
    { path: 'categories/defaults', element: <SetupDefaultCategoriesPage /> },
]

const calendarSyncRoutes: RouteObject[] = [
    { path: 'calendar/sync', element: <SetupCalendarSyncPage /> },
    { path: 'calendar/setup/:credentialID', element: <ExternalCalendarSelectPage /> },
]

const linkRoutes: RouteObject[] = [
    { path: 'link', element: <SetupSuiteLinkPage /> },
    { path: 'link/new', element: <LinkPage /> },
    { path: 'link/:linkID/edit', element: <LinkPage /> },
]

const leadsRoutes: RouteObject[] = [
    { path: 'leads', element: <SetupOnlineLeadPage /> },
    { path: 'leads/questions', element: <FieldsPage forClient={true} /> },
    { path: 'leads/questions/new', element: <ClientFieldPage /> },
    { path: 'leads/questions/:fieldID', element: <ClientFieldPage /> },
]

const contractsRoutes: RouteObject[] = [
    { path: 'contracts', element: <SetupContractsPage /> },
    { path: 'contracts/new', element: <ContractWorkflow action="new" /> },
    { path: 'contracts/:id/edit', element: <ContractWorkflow action="edit" /> },
    { path: 'contracts/:id/copy', element: <ContractWorkflow action="copy" /> },
    { path: 'contracts/defaults', element: <SetupDefaultContractsPage /> },
]

const formsRoutes: RouteObject[] = [
    { path: 'forms', element: <SetupTypesPage /> },
    { path: 'forms/new', element: <TypeWorkflow action="new" /> },
    { path: 'forms/:id/edit', element: <TypeWorkflow action="edit" /> },
    { path: 'forms/:id/copy', element: <TypeWorkflow action="copy" /> },
    { path: 'forms/defaults', element: <SetupDefaultFormsPage /> },
    { path: 'forms/:formID?/questions', element: <FieldsPage /> },
    { path: 'forms/:formID?/questions/new', element: <FieldPage /> },
    {
        path: 'forms/:formID/questions/:fieldID?/edit',
        element: <FieldPage />,
    },
]

const paymentsRoutes: RouteObject[] = [
    { path: 'payments/sales-tax', element: <SetupSalesTaxPage /> },
    { path: 'payments/gratuity', element: <SetupGratuityPage /> },
    { path: 'payments/payout', element: <SetupPayoutsPage /> },
    { path: 'payments/methods', element: <SourcesPage /> },
    { path: 'payments/:recordType/:recordID', element: <PaymentsPage /> },
]

const readerRoutes: RouteObject[] = [
    { path: 'reader', element: <ReadersPage /> },
    { path: 'reader/add', element: <ReaderPage /> },
    { path: 'reader/checkout', element: <OrderReaderPage /> },
    { path: 'reader/:readerID/edit', element: <ReaderPage /> },
    { path: 'reader/:readerID/activate', element: <ReaderPage /> },
]

const changePlanRoutes: RouteObject[] = [
    { path: 'change-plan', element: <SelectPlanPage context="change" /> },
    { path: 'change-plan/cancel', element: <DowngradePlanPage /> },
    { path: 'change-plan/cancel/features', element: <DowngradeFeaturesPage /> },
    { path: 'change-plan/cancel/lite', element: <DowngradeLitePage /> },
    { path: 'change-plan/cancel/feedback', element: <DowngradeReasonPage /> },
    { path: 'change-plan/cancel/confirm', element: <DowngradeConfirmationPage /> },
    { path: 'change-plan/cancel/discount', element: <DowngradeDiscountPage /> },
]

const settingsRoutes: RouteObject = {
    path: 'settings',
    element: (
        <div className="relative h-screen w-full overflow-y-auto">
            <Outlet />
        </div>
    ),
    children: [
        ...accountRoutes,
        ...servicesRoutes,
        ...classesRoutes,
        ...reservationsRoutes,
        ...productsRoutes,
        ...packagesRoutes,
        ...subscriptionsRoutes,
        ...giftsRoutes,
        ...categoriesRoutes,
        ...calendarSyncRoutes,
        ...linkRoutes,
        ...leadsRoutes,
        ...contractsRoutes,
        ...formsRoutes,
        ...paymentsRoutes,
        ...readerRoutes,
        ...changePlanRoutes,

        { path: '', element: <SettingsPage /> },
        { path: 'sort', element: <SetupItemsSortPage /> },
        { path: 'scheduling', element: <SetupSchedulingPage /> },
        { path: 'cancellations', element: <SetupCancellationPage /> },
        {
            path: 'videoconferencing',
            element: (
                <PlanRoute>
                    <SetupVideoConferencePage />
                </PlanRoute>
            ),
        },
        { path: 'chat', element: <SetupOnlineChatPage /> },
        { path: 'payments', element: <SetupOnlinePaymentPage /> },
        { path: 'tutorials', element: <TutorialsPage /> },
        { path: 'tax-form', element: <SendTaxFormPage /> },
        { path: 'data-export', element: <SetupDataExportPage /> },
        {
            path: 'request-call',
            element: <RequestCallPage forDashboard={false} isOpen={true} />,
        },
        { path: 'privacy-terms', element: <PrivacyAndTermsPage /> },

        { path: 'upgrade-team-plan', element: <SelectPlanPage context="team" /> },
        {
            path: 'upgrade-affiliate-plan',
            element: <SelectPlanPage context="affiliate" />,
        },
    ],
}

// Navigation Routes

const dashboardsRoutes = [
    { path: ':dashboard', element: <DashboardDetailPage /> },
    {
        path: 'deposits/balance',
        element: <DashboardDetailDepositsPage />,
        loader: dashboardDetailDepositsPageLoader,
    },
]

const reportsRoutes = [
    { path: 'reports/:reportId?', element: <ReportRunnerPage /> },
    { path: 'reports/:reportId/details', element: <ReportDetailsPage /> },
]

const messagesRoutes: RouteObject[] = [
    {
        path: 'messages',
        element: <MessagesPageWrapper />,
        loader: messagesPageLoader,
        children: [
            { path: ':threadID', element: <MessageThreadWrapper />, loader: messageThreadLoader },
        ],
    },
]

const alertRoutes: RouteObject = {
    path: 'alert',
    element: <AlertsRoot />,
    children: [
        { path: 'affiliate', element: <AlertAffiliatePage /> },
        { path: 'reader', element: <AlertReaderPage /> },
        { path: 'asap', element: <AlertASAPPage /> },
        { path: 'team', element: <AlertTeamPage /> },
    ],
}

const navigationRoutes: RouteObject[] = [
    ...reportsRoutes,
    alertRoutes,

    // TODO: deprecate
    { path: 'record/:recordType/:recordID', element: <BaseRecordPage /> },
]

const recordRoutes: RouteObject[] = [
    { path: '/gifts/:id', element: <GiftPage /> },
    { path: '/clients/:id', element: <ClientPage /> },
    { path: '/subaccounts/:id', element: <SubaccountPage /> },
    { path: '/lessons/:id', element: <LessonPage /> },
    { path: '/reservations/:id', element: <FixedReservationPage /> },
    { path: '/packages/:id', element: <PackagePage /> },
    { path: '/subscriptions/:id', element: <SubscriptionPage /> },
    { path: '/invoices/:id', element: <InvoicePage /> },
    { path: '/forms/:id', element: <RecordPage /> },
    { path: '/contracts/:id', element: <SignaturePage /> },
    { path: '/team-members/:id', element: <PayeePage /> },
    { path: '/deposits/:id', element: <DepositPage /> },
    { path: '/estimates/:id', element: <EstimatePage /> },
    { path: '/payments/:id', element: <PaymentPage /> },
    { path: '/charges/:id', element: <ChargePage /> },
    { path: '/bookings/:id', element: <BookingPage /> },
    { path: '/events/:id', element: <EventPage /> },
]

// Workflow Routes

const chargeRoutes: RouteObject[] = [
    { path: 'charge/new', element: <ChargeWorkflow action="new" /> },
    { path: 'charge/:id/copy', element: <ChargeWorkflow action="copy" /> },
    { path: 'charge/:recordType/:recordID/:subType?', element: <ChargeWorkflow action="charge" /> },
]

const invoiceRoutes: RouteObject[] = [
    { path: 'invoices/new', element: <InvoiceWorkflow action="new" /> },
    { path: 'invoices/:id/edit', element: <InvoiceWorkflow action="edit" /> },
    { path: 'invoices/:id/copy', element: <InvoiceWorkflow action="copy" /> },
]

const estimateRoutes: RouteObject[] = [
    { path: 'estimates/new', element: <EstimateWorkflow action="new" /> },
    { path: 'estimates/:id/edit', element: <EstimateWorkflow action="edit" /> },
    { path: 'estimates/:id/copy', element: <EstimateWorkflow action="copy" /> },
]

const scheduleRoutes: RouteObject[] = [
    { path: 'bookings/new', element: <BookingWorkflow action="new" /> },
    { path: 'bookings/:id/edit', element: <BookingWorkflow action="edit" /> },
    { path: 'bookings/:id/rebook', element: <BookingWorkflow action="copy" /> },
]

const packageRoutes: RouteObject[] = [
    { path: 'packages/new', element: <PackageWorkflow action="new" /> },
    { path: 'packages/:id/edit', element: <PackageWorkflow action="edit" /> },
    { path: 'packages/:id/copy', element: <PackageWorkflow action="copy" /> },
]

const subscriptionRoutes: RouteObject[] = [
    { path: 'subscriptions/new', element: <SubscriptionWorkflow action="new" /> },
    { path: 'subscriptions/:id/edit', element: <SubscriptionWorkflow action="edit" /> },
    { path: 'subscriptions/:id/copy', element: <SubscriptionWorkflow action="copy" /> },
]

const giftRoutes: RouteObject[] = [
    { path: 'gifts/new', element: <GiftWorkflow action="new" /> },
    { path: 'gifts/:id/edit', element: <GiftWorkflow action="edit" /> },
    { path: 'gifts/:id/copy', element: <GiftWorkflow action="copy" /> },
]

// const orderRoutes: RouteObject[] = [
//     { path: 'order/new', element: <NewOrderPage /> },
//     { path: 'order/view/:orderID', element: <OrderPage /> },
//     { path: 'order/edit/:orderID?', element: <NewOrderPage /> },
// ]

const inventoryRoutes: RouteObject[] = [
    { path: 'inventory/:itemID/new', element: <AdjustmentPage action="new" /> },
    { path: 'inventory/:adjustmentID/edit', element: <AdjustmentPage action="edit" /> },
    { path: 'inventory/history', element: <AdjustmentHistoryPage /> },
    { path: 'inventory/product/:productID?', element: <SetupInventoryPage /> },
]

const blockRoutes: RouteObject[] = [
    { path: 'blocks/new', element: <BlockSchedule action="new" /> },
    { path: 'blocks/:id/edit', element: <BlockSchedule action="edit" /> },
]

const clientRoutes: RouteObject[] = [
    { path: 'client/:id/add-card', element: <ClientCardPage /> },
    { path: 'clients/new', element: <UserWorkflow action="new" /> },
    { path: 'clients/:id/edit', element: <UserWorkflow action="edit" /> },
]

const subaccountRoutes: RouteObject[] = [
    { path: 'subaccounts/new', element: <SubaccountWorkflow action="new" /> },
    { path: 'subaccounts/:id/edit', element: <SubaccountWorkflow action="edit" /> },
]

const goliveRoutes: RouteObject = {
    path: '/golive',
    element: (
        <OnboardingProvider>
            <OnboardingRoot />
        </OnboardingProvider>
    ),
    children: [
        { path: 'welcome', element: <SetupDashboardVerifyWelcomePage /> },
        { path: 'business-info', element: <SetupDashboardVerifyBusinessPage /> },
        { path: 'personal-info', element: <SetupDashboardVerifyPersonalPage /> },
        { path: 'photo-verification', element: <SetupDashboardVerifyPhotoPage /> },
        {
            path: 'conditionally-verified',
            element: <SetupDashboardVerifyCompletePage />,
        },
        { path: 'receive-payouts', element: <SetupDashboardPayoutPage /> },
        { path: 'success', element: <SetupDashboardCompletePage /> },
    ],
}

const onboardingRoutes: RouteObject = {
    path: '/onboarding',
    element: (
        <OnboardingProvider>
            <OnboardingRoot />
        </OnboardingProvider>
    ),
    children: [
        { path: 'hours', element: <SetupOnboardingBusinessHoursPage /> },
        { path: 'copy', element: <SetupOnboardingCopyDataPage /> },
        { path: 'categories', element: <SetupOnboardingCategoriesPage /> },
        { path: 'services', element: <SetupOnboardingItemsPage /> },
        { path: 'products', element: <SetupOnboardingItemsPage /> },
        { path: 'classes', element: <SetupOnboardingItemsPage /> },
        { path: 'reservations', element: <SetupOnboardingItemsPage /> },
        { path: 'packages', element: <SetupOnboardingItemsPage /> },
        { path: 'subscriptions', element: <SetupOnboardingItemsPage /> },
        { path: 'gifts', element: <SetupOnboardingItemsPage /> },
        { path: 'forms', element: <SetupOnboardingTypesPage /> },
        { path: 'contracts', element: <SetupOnboardingContractsPage /> },
        { path: 'intake', element: <SetupOnboardingClientIntakePage /> },
        { path: 'location', element: <SetupOnboardingLocationPage /> },
        { path: 'payment', element: <SetupOnboardingPaymentsPage /> },
        { path: 'payment-continued', element: <SetupOnboardingPaymentsContinuedPage /> },
        { path: 'profile', element: <SetupOnboardingProfilePage /> },
        { path: 'success', element: <SetupOnboardingSuccessPage /> },
    ],
}

const surveyRoutes: RouteObject = {
    path: '/survey/product-feedback',
    element: <NPSWorkflowCoordinator />,
}

const workflowRoutes: RouteObject[] = [
    ...chargeRoutes,
    ...invoiceRoutes,
    ...estimateRoutes,
    ...scheduleRoutes,
    ...packageRoutes,
    ...subscriptionRoutes,
    ...giftRoutes,
    // ...orderRoutes, // TODO: add back when orders project is complete
    ...inventoryRoutes,
    ...blockRoutes,
    ...clientRoutes,
    ...subaccountRoutes,
    onboardingRoutes,
    goliveRoutes,
    surveyRoutes,

    { path: 'contracts/send', element: <SendContract /> },
    { path: 'forms/send', element: <NewSendRecordPage /> },
    { path: 'confirm-charge/subscription/:recordID', element: <NewSubscriptionTotalPage /> },
]

export const router = createBrowserRouter(
    [
        {
            path: '/',
            hydrateFallbackElement: <></>, // gets rid of hydration error
            element: <Outlet />,
            errorElement: <ErrorRouteNotFound />,
            children: [
                {
                    path: '/login',
                    element: <LoginPage />,
                    children: [
                        { path: '', element: <Login /> },
                        { path: 'code', element: <CodeEntry /> },
                    ],
                },
                {
                    path: '/logout',
                    element: <Logout />,
                },
                { path: 'signup-plan', element: <NewUserSelectPlan /> },
                {
                    path: '/',
                    element: <App />,
                    loader: async () => {
                        // if we are running this directly from the login page, we will already
                        // have a user in the cache/DB, so we can return early
                        const user = PSApplication.me()
                        if (user) {
                            return { initComplete: true, user }
                        }

                        // otherwise, we need to initialize the DB and sync the user
                        // we will fall here on a refresh

                        await SQLiteFactory.getInstance().initializeSql()

                        await Promise.all([
                            RecordFactory.getInstance().fetchUser(PSApplication.userId()),
                            RecordFactory.getInstance().sync(PSApplication.userId()),
                        ])

                        await initializeCache()

                        return { initComplete: true, user: PSApplication.me() }
                    },
                    shouldRevalidate: () => {
                        // never rerun the root loader on navigate
                        // refreshes will always run the loader
                        return false
                    },

                    children: [
                        {
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: [
                                {
                                    path: '',
                                    index: true,
                                    element: (
                                        <ProtectedRoute>
                                            <DashboardPage />
                                        </ProtectedRoute>
                                    ),
                                },
                                { path: 'dashboard/user/:userId', element: <DashboardPage /> },
                                {
                                    path: 'dashboard/user/:userId/subaccount/:subaccountId',
                                    element: <DashboardPage />,
                                },
                            ],
                        },
                        {
                            path: '/login/accounts',
                            element: <ComebackAccountSwitcher />,
                        },
                        {
                            path: 'dashboards',
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: dashboardsRoutes,
                        },
                        {
                            element: <NavigationRoot />,
                            children: navigationRoutes,
                        },
                        {
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: [{ path: 'clients', element: <ClientsPage /> }],
                        },
                        {
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: [settingsRoutes],
                        },
                        {
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: recordRoutes,
                        },
                        {
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: workflowRoutes,
                        },
                        {
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: messagesRoutes,
                        },
                        {
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: [
                                {
                                    path: 'calendar',
                                    element: <SchedulePage />,
                                },
                            ],
                        },
                        {
                            element: <NavigationRoot shouldDisableOutletLayout />,
                            children: [
                                {
                                    path: 'signup',
                                    element: <SignupWorkflow />,
                                },
                            ],
                        },
                        ...(IS_DEVELOPMENT || IS_STAGING ? designRoutes : []),
                    ],
                },
            ],
        },
    ],
    {
        future: {
            v7_relativeSplatPath: true,
            v7_fetcherPersist: true,
            v7_normalizeFormMethod: true,
            v7_partialHydration: true,
            v7_skipActionErrorRevalidation: true,
        },
    }
)
